// =====================================================================
// useClinicUnits (Unidades do Pablo — modelo B / S90)
// Busca as unidades (clinic_units) da company do token uma única vez.
// Companies SEM unidades (CLEE/Rostand/+51) => units=[] => sem seletor
// (as telas se comportam como hoje). Falha silenciosa => units=[] (fail-open).
// Persiste a unidade ativa por company no localStorage (sobrevive a reload).
// =====================================================================
import { useState, useEffect, useCallback } from "react";
import { listClinicUnits } from "../services/financeService";

const storageKey = (companyId) => `unitScope:${companyId || "x"}`;

export default function useClinicUnits(companyId, isAdmin = false) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeUnit, setActiveUnitState] = useState(undefined);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await listClinicUnits();
        const list = Array.isArray(data && data.units) ? data.units : [];
        if (!alive) return;
        setUnits(list);
        if (list.length > 0) {
          // Restaura a última unidade escolhida (se ainda válida), senão a 1ª.
          let saved;
          try {
            saved = window.localStorage.getItem(storageKey(companyId));
          } catch (e) {
            saved = null;
          }
          const validIds = list.map((u) => String(u.id));
          // 'all' (Caixa Geral) só pode ser restaurado se o user for admin — evita
          // que, num PC compartilhado, um não-admin herde o 'all' de um admin (→ 403).
          const restored =
            saved && ((saved === "all" && isAdmin) || validIds.includes(saved))
              ? saved
              : null;
          setActiveUnitState(
            restored != null
              ? restored === "all"
                ? "all"
                : Number(restored)
              : Number(list[0].id)
          );
        }
      } catch (e) {
        if (alive) setUnits([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId, isAdmin]);

  const setActiveUnit = useCallback(
    (val) => {
      setActiveUnitState(val);
      try {
        window.localStorage.setItem(storageKey(companyId), String(val));
      } catch (e) {
        /* ignora quota/privacidade */
      }
    },
    [companyId]
  );

  return {
    units,
    loading,
    hasUnits: units.length > 0,
    activeUnit, // number (unidade) | 'all' (Caixa Geral) | undefined (sem unidades)
    setActiveUnit
  };
}
